// src/components/Queue/FichaModal.js
// ---------------------------------------------------------------------------
// Sesión A (S3b ampliado · 2026-09-06) · "estación en la ficha" (D14).
//
// La ficha de IMPRESIÓN o CORTE abre este modal: lista las planchas del job que
// pasan por la estación, con su avance (impresas o cortadas / repeticiones) y,
// por plancha, +1 · +N · −1. Arriba, un campo que recibe el código del escáner
// (teclado HID, Enter = enviar) y lo resuelve SOLO contra las planchas de este
// job; un código ajeno se rechaza sin escribir. Selector de máquina (default =
// la de la ficha) que viaja con cada avance.
//
// Nada se escribe desde el cliente: todo va por la callable recordProductionScan
// (permiso canScanProduction O canManageQueue), con `source` 'cola' (botones) o
// 'escaneo' (campo del escáner). El −1 pide confirmación y el servidor lo loguea.
// La lógica pura vive en src/lib/fichaSheets.js.
// ---------------------------------------------------------------------------
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { X, ScanLine, Plus, Minus, ExternalLink, Printer, Scissors, Check, AlertTriangle } from 'lucide-react';
import { functions } from '../../firebase/firebase-config';
import { STATION_LABEL, machinesForStation } from '../../lib/workQueue.js';
import {
    sheetsForStation, typeForStation, countOf, faltante, deltaError, canUndo, resolveScanInJob,
} from '../../lib/fichaSheets';
import { repsOf, isTwoSided } from '../../lib/production';

const TYPE_LABEL = { print: 'impresas', printBack: 'dorso', cut: 'cortadas' };

// Una fila de contador (frente, dorso o corte) de una plancha, con sus 3 botones.
const CounterRow = ({ sheet, type, label, canAct, busy, onAdvance, onUndo, opts }) => {
    const [nOpen, setNOpen] = useState(false);
    const [n, setN] = useState('');
    const done = countOf(sheet, type);
    const reps = repsOf(sheet);
    const falta = faltante(sheet, type, opts);
    const pct = reps > 0 ? Math.round((Math.min(done, reps) / reps) * 100) : 0;
    const nErr = nOpen && n !== '' ? deltaError(sheet, type, Number(n), opts) : '';
    const isBusy = busy === `${sheet.id}:${type}`;
    const complete = falta === 0 && done >= reps;

    const enviarN = () => {
        const d = Number(n);
        if (!n || deltaError(sheet, type, d, opts)) return;
        onAdvance(sheet, type, d);
        setN(''); setNOpen(false);
    };

    return (
        <div className="mt-2" data-testid={`row-${sheet.id}-${type}`}>
            <div className="flex items-center gap-2">
                <span className="w-16 text-[11px] uppercase tracking-wide text-gray-500 flex-shrink-0">{label}</span>
                <span className="font-mono text-lg font-bold tabular-nums text-gray-800 flex-shrink-0 w-24 text-center">
                    {done}<span className="text-gray-400 text-sm">/{reps}</span>
                </span>
                <span className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden min-w-[40px]">
                    <span className={`block h-full ${complete ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${pct}%` }} />
                </span>
                <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                        type="button"
                        onClick={() => onUndo(sheet, type)}
                        disabled={!canAct || isBusy || !canUndo(sheet, type, opts)}
                        title="Deshacer 1"
                        aria-label={`Deshacer 1 ${TYPE_LABEL[type]} ${sheet.hri || sheet.id}`}
                        className="h-11 w-11 rounded-lg border border-red-200 text-red-600 bg-white hover:bg-red-50 disabled:opacity-30 flex items-center justify-center"
                    >
                        <Minus size={18} />
                    </button>
                    <button
                        type="button"
                        onClick={() => onAdvance(sheet, type, 1)}
                        disabled={!canAct || isBusy || falta < 1}
                        title="Sumar 1"
                        aria-label={`Sumar 1 ${TYPE_LABEL[type]} ${sheet.hri || sheet.id}`}
                        className="h-11 w-14 rounded-lg bg-blue-600 text-white font-bold hover:bg-blue-700 disabled:opacity-30 flex items-center justify-center gap-0.5"
                    >
                        <Plus size={18} />1
                    </button>
                    <button
                        type="button"
                        onClick={() => { setNOpen((v) => !v); setN(''); }}
                        disabled={!canAct || isBusy || falta < 1}
                        title="Sumar varias"
                        aria-label={`Sumar varias ${TYPE_LABEL[type]} ${sheet.hri || sheet.id}`}
                        className={`h-11 w-14 rounded-lg font-bold border disabled:opacity-30 flex items-center justify-center gap-0.5 ${nOpen ? 'bg-blue-100 border-blue-400 text-blue-800' : 'bg-white border-blue-300 text-blue-700 hover:bg-blue-50'}`}
                    >
                        <Plus size={18} />N
                    </button>
                </div>
            </div>
            {nOpen && (
                <div className="mt-1.5 ml-16 flex items-center gap-2">
                    <input
                        type="number"
                        min="1"
                        max={falta}
                        inputMode="numeric"
                        value={n}
                        onChange={(e) => setN(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') enviarN(); }}
                        placeholder={`hasta ${falta}`}
                        aria-label={`Cantidad a sumar ${sheet.hri || sheet.id}`}
                        className="h-11 w-28 border rounded-lg px-3 text-lg font-mono text-center"
                        autoFocus
                    />
                    <button
                        type="button"
                        onClick={enviarN}
                        disabled={!n || !!nErr || isBusy}
                        aria-label={`Confirmar sumar ${sheet.hri || sheet.id}`}
                        className="h-11 px-4 rounded-lg bg-blue-600 text-white font-bold disabled:opacity-30"
                    >
                        Sumar {n && !nErr ? n : ''}
                    </button>
                    <span className="text-xs text-gray-500">faltan {falta}</span>
                    {nErr && <span className="text-xs text-red-600">{nErr}</span>}
                </div>
            )}
        </div>
    );
};

// `cutBeforePrint`: la máquina de IMPRESIÓN de la ficha del job lleva
// `allowsCutBeforePrint` (Single Pass Postmark) → se puede cortar antes de imprimir.
const FichaModal = ({ ficha, job, machines = [], db, user, onClose, onOpenJob, cutBeforePrint = false }) => {
    const station = ficha?.station;
    const jobId = ficha?.jobId;
    const [sheets, setSheets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [machineId, setMachineId] = useState(ficha?.machineId || '');
    const [face, setFace] = useState('front');
    const [scan, setScan] = useState('');
    const [busy, setBusy] = useState('');
    const [msg, setMsg] = useState(null);           // { kind: 'ok'|'err', text }
    const [confirmUndo, setConfirmUndo] = useState(null);   // { sheet, type }
    const scanRef = useRef(null);

    const canAct = !!(user?.permissions?.canScanProduction || user?.permissions?.canManageQueue);
    const source = 'cola';
    const opts = useMemo(() => ({ cutBeforePrint: !!cutBeforePrint }), [cutBeforePrint]);

    useEffect(() => {
        if (!db || !jobId) return undefined;
        // Ruta anidada del job (no collectionGroup): rules + índices (CLAUDE.md §3.4).
        const unsub = onSnapshot(collection(db, `jobs/${jobId}/sheets`), (snap) => {
            setSheets(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
            setLoading(false);
        }, (e) => {
            console.error('Error leyendo planchas de la ficha:', e);
            setMsg({ kind: 'err', text: 'No se pudieron cargar las planchas.' });
            setLoading(false);
        });
        return () => unsub();
    }, [db, jobId]);

    const visibles = useMemo(() => sheetsForStation(sheets, station), [sheets, station]);
    const hayDosCaras = useMemo(() => station === 'IMPRESION' && visibles.some(isTwoSided), [visibles, station]);
    const maquinas = useMemo(() => machinesForStation(machines, station), [machines, station]);
    const machineName = maquinas.find((m) => m.id === machineId)?.name || '';

    const focusScan = useCallback(() => { try { scanRef.current?.focus(); } catch (_) { /* sin DOM */ } }, []);

    const registrar = useCallback(async (sheet, type, delta, src) => {
        const err = deltaError(sheet, type, delta, opts);
        if (err) { setMsg({ kind: 'err', text: `${sheet.hri || sheet.id}: ${err}` }); return; }
        setBusy(`${sheet.id}:${type}`);
        setMsg(null);
        try {
            const fn = httpsCallable(functions, 'recordProductionScan');
            const res = await fn({ jobId, sheetId: sheet.id, type, delta, machineId: machineId || null, source: src });
            const newCount = res?.data?.newCount;
            const verbo = delta < 0 ? `deshecho ${-delta}` : `+${delta}`;
            setMsg({ kind: 'ok', text: `${sheet.hri || sheet.id}: ${verbo} → ${newCount ?? '?'}/${repsOf(sheet)} ${TYPE_LABEL[type]}${machineName ? ` · ${machineName}` : ''}` });
        } catch (e) {
            console.error('recordProductionScan falló:', e);
            setMsg({ kind: 'err', text: `${sheet.hri || sheet.id}: ${e?.message || 'no se pudo registrar.'}` });
        } finally {
            setBusy('');
            focusScan();
        }
    }, [jobId, machineId, machineName, focusScan, opts]);

    const onAdvance = useCallback((sheet, type, delta) => registrar(sheet, type, delta, source), [registrar]);
    const onUndo = useCallback((sheet, type) => setConfirmUndo({ sheet, type }), []);
    const confirmarUndo = useCallback(async () => {
        const c = confirmUndo;
        setConfirmUndo(null);
        if (c) await registrar(c.sheet, c.type, -1, source);
    }, [confirmUndo, registrar]);

    const onScanSubmit = useCallback(async (e) => {
        if (e) e.preventDefault();
        const raw = scan;
        setScan('');
        if (!raw.trim()) return;
        const r = resolveScanInJob({ raw, jobId, sheets, station, face });
        if (!r.ok) {
            setMsg({ kind: 'err', text: `Rechazado: ${r.reason}` });
            focusScan();
            return;
        }
        await registrar(r.sheet, r.type, 1, 'escaneo');
    }, [scan, jobId, sheets, station, face, registrar, focusScan]);

    if (!ficha) return null;
    const StationIcon = station === 'CORTE' ? Scissors : Printer;

    return (
        <div className="fixed inset-0 z-[110] bg-black bg-opacity-60 flex items-end md:items-center justify-center p-0 md:p-4" onClick={onClose} data-testid="ficha-modal">
            <div
                className="relative bg-white w-full md:max-w-2xl md:rounded-xl rounded-t-xl shadow-2xl max-h-[95vh] flex flex-col overflow-hidden"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-label={`Ficha ${ficha.jobNumber || ficha.jobId} · ${STATION_LABEL[station] || station}`}
            >
                {/* Cabecera */}
                <div className="flex items-start gap-3 p-4 border-b flex-shrink-0">
                    {job?.coverImageUrl ? (
                        <img src={job.coverImageUrl} alt="" className="w-14 h-14 rounded-md object-cover bg-gray-100 flex-shrink-0" />
                    ) : (
                        <div className="w-14 h-14 rounded-md bg-gray-100 flex items-center justify-center text-gray-300 flex-shrink-0"><StationIcon size={20} /></div>
                    )}
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                            <span className="font-mono font-bold text-xl text-gray-800">{ficha.jobNumber || ficha.jobId}</span>
                            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 uppercase">{STATION_LABEL[station] || station}</span>
                        </div>
                        {job?.jobName && <p className="text-sm text-gray-700 truncate" title={job.jobName}>{job.jobName}</p>}
                        <p className="text-xs text-gray-500 truncate">{ficha.clientName || 'Sin cliente'} · ficha {Number(ficha.sheetsDone) || 0}/{Number(ficha.sheetsTotal) || 0}</p>
                    </div>
                    <button type="button" onClick={onClose} aria-label="Cerrar" className="h-11 w-11 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500"><X size={22} /></button>
                </div>

                {/* Máquina + escáner */}
                <div className="p-4 border-b flex-shrink-0 space-y-3 bg-gray-50">
                    <div className="flex flex-wrap items-center gap-3">
                        <label className="text-xs font-semibold text-gray-600 flex items-center gap-2">
                            Máquina
                            <select
                                value={machineId}
                                onChange={(e) => setMachineId(e.target.value)}
                                aria-label="Máquina"
                                className="h-11 border rounded-lg px-2 text-sm bg-white min-w-[160px]"
                            >
                                <option value="">Sin máquina</option>
                                {maquinas.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                            </select>
                        </label>
                        {hayDosCaras && (
                            <div className="flex items-center gap-1 text-xs font-semibold text-gray-600" role="group" aria-label="Cara">
                                <span>Cara</span>
                                {[['front', 'Frente'], ['back', 'Dorso']].map(([k, l]) => (
                                    <button
                                        key={k}
                                        type="button"
                                        onClick={() => setFace(k)}
                                        className={`h-9 px-3 rounded-lg border ${face === k ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600'}`}
                                    >
                                        {l}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <form onSubmit={onScanSubmit} className="flex items-center gap-2">
                        <ScanLine size={20} className="text-gray-400 flex-shrink-0" />
                        <input
                            ref={scanRef}
                            value={scan}
                            onChange={(e) => setScan(e.target.value)}
                            disabled={!canAct}
                            placeholder={canAct ? 'Escanea o escribe el código de la plancha y pulsa Enter' : 'Sin permiso para registrar avance'}
                            aria-label="Código de plancha"
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck={false}
                            className="flex-1 h-12 border rounded-lg px-3 font-mono text-base bg-white disabled:bg-gray-100"
                        />
                        <button type="submit" disabled={!canAct || !scan.trim()} className="h-12 px-4 rounded-lg bg-gray-800 text-white font-bold disabled:opacity-30">Enviar</button>
                    </form>
                    {msg && (
                        <p
                            role={msg.kind === 'err' ? 'alert' : 'status'}
                            className={`text-sm font-semibold flex items-center gap-2 ${msg.kind === 'err' ? 'text-red-700' : 'text-green-700'}`}
                        >
                            {msg.kind === 'err' ? <AlertTriangle size={16} /> : <Check size={16} />}{msg.text}
                        </p>
                    )}
                    {!canAct && (
                        <p className="text-xs text-gray-500">Registrar avance requiere el permiso de escaneo o de gestión de la cola.</p>
                    )}
                    {cutBeforePrint && station === 'CORTE' && (
                        <p className="text-xs text-indigo-700" data-testid="aviso-cut-before-print">Máquina de impresión con corte antes de impresión: el corte no está limitado por lo impreso.</p>
                    )}
                </div>

                {/* Planchas */}
                <div className="p-4 overflow-y-auto flex-1">
                    {loading ? (
                        <p className="text-center text-gray-500 py-6">Cargando planchas…</p>
                    ) : visibles.length === 0 ? (
                        <p className="text-center text-gray-500 py-6">Este trabajo no tiene planchas para {STATION_LABEL[station] || station}.</p>
                    ) : (
                        <ul className="space-y-3">
                            {visibles.map((s) => (
                                <li key={s.id} data-testid={`plancha-${s.id}`} className="border rounded-lg p-3">
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="font-mono font-bold text-gray-800">{s.hri || s.id}</span>
                                        {s.productionStatus && <span className="text-[11px] text-gray-500">{s.productionStatus}</span>}
                                    </div>
                                    {station === 'IMPRESION' && isTwoSided(s) ? (
                                        <>
                                            <CounterRow sheet={s} type="print" label="Frente" canAct={canAct} busy={busy} onAdvance={onAdvance} onUndo={onUndo} opts={opts} />
                                            <CounterRow sheet={s} type="printBack" label="Dorso" canAct={canAct} busy={busy} onAdvance={onAdvance} onUndo={onUndo} opts={opts} />
                                        </>
                                    ) : (
                                        <CounterRow sheet={s} type={typeForStation(station, s)} label={station === 'CORTE' ? 'Corte' : 'Impresas'} canAct={canAct} busy={busy} onAdvance={onAdvance} onUndo={onUndo} opts={opts} />
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="p-3 border-t flex items-center justify-between flex-shrink-0">
                    <span className="text-xs text-gray-400">Todo avance queda en el historial del trabajo.</span>
                    <button type="button" onClick={() => onOpenJob && onOpenJob(jobId)} className="h-11 px-4 rounded-lg border text-sm font-semibold text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                        <ExternalLink size={16} /> Abrir trabajo
                    </button>
                </div>

                {confirmUndo && (
                    <div className="absolute inset-0 z-[120] bg-black bg-opacity-40 flex items-center justify-center p-4" onClick={() => setConfirmUndo(null)}>
                        <div className="bg-white rounded-xl p-5 max-w-sm w-full shadow-xl" onClick={(e) => e.stopPropagation()} role="alertdialog" aria-label="Confirmar deshacer">
                            <p className="font-bold text-gray-800">¿Deshacer 1 {TYPE_LABEL[confirmUndo.type]} de {confirmUndo.sheet.hri || confirmUndo.sheet.id}?</p>
                            <p className="text-sm text-gray-500 mt-1">
                                Pasa de {countOf(confirmUndo.sheet, confirmUndo.type)} a {countOf(confirmUndo.sheet, confirmUndo.type) - 1}. Queda registrado con tu usuario.
                            </p>
                            <div className="mt-4 flex gap-2 justify-end">
                                <button type="button" onClick={() => setConfirmUndo(null)} className="h-11 px-4 rounded-lg border text-sm font-semibold">Cancelar</button>
                                <button type="button" onClick={confirmarUndo} className="h-11 px-4 rounded-lg bg-red-600 text-white text-sm font-bold">Sí, deshacer</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FichaModal;
